import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  Loader2,
  Clock,
  CheckCircle,
  Activity,
  Mail,
  MessageSquare,
  ChevronRight,
  UserCheck,
  UserPlus,
  XCircle,
  Package,
  Building2,
  AlertCircle,
  ClipboardList,
  Ticket,
  Eye,
  EyeOff,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [todayClasses, setTodayClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRevenue, setShowRevenue] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, activitiesRes, todayRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`),
        axios.get(`${API}/admin/activities?limit=9`),
        axios.get(`${API}/admin/dashboard/today-classes`),
      ]);
      
      setStats(statsRes.data);
      setActivities(activitiesRes.data.activities);
      setTodayClasses(todayRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Today's Revenue",
      value: `KES ${(stats?.today_revenue || 0).toLocaleString()}`,
      subtitle: `${stats?.today_bookings || 0} paid bookings today`,
      icon: DollarSign,
      color: "text-green-400",
      bgColor: "bg-green-500/20",
      sensitive: true,
    },
    {
      title: "Monthly Revenue",
      value: `KES ${(stats?.monthly_revenue || 0).toLocaleString()}`,
      subtitle: `${stats?.monthly_bookings || 0} paid bookings this month`,
      icon: TrendingUp,
      color: "text-empire-gold",
      bgColor: "bg-empire-gold/20",
      sensitive: true,
    },
    {
      title: "Unpaid Leads",
      value: stats?.unpaid_leads_month || 0,
      subtitle: "Started checkout, never paid",
      icon: UserPlus,
      color: "text-orange-400",
      bgColor: "bg-orange-500/20",
      link: "/admin/bookings",
    },
    {
      title: "Pending Sessions",
      value: stats?.pending_private_sessions || 0,
      subtitle: "Awaiting confirmation",
      icon: Clock,
      color: "text-yellow-400",
      bgColor: "bg-yellow-500/20",
      link: "/admin/private-sessions",
    },
    {
      title: "Pending Studio",
      value: stats?.pending_studio_bookings || 0,
      subtitle: "Studio bookings",
      icon: Building2,
      color: "text-purple-400",
      bgColor: "bg-purple-500/20",
      link: "/admin/studio-bookings",
    },
    {
      title: "Open Tickets",
      value: stats?.open_support_tickets || 0,
      subtitle: "Support tickets",
      icon: AlertCircle,
      color: "text-red-400",
      bgColor: "bg-red-500/20",
      link: "/admin/support",
    },
    {
      title: "Campaigns",
      value: stats?.total_campaigns || 0,
      subtitle: "Total campaigns",
      icon: Mail,
      color: "text-blue-400",
      bgColor: "bg-blue-500/20",
      link: "/admin/campaigns",
    },
  ];

  const activityIcons = {
    booking_created: Users,
    booking_confirmed: CheckCircle,
    booking_cancelled: XCircle,
    payment_received: DollarSign,
    check_in: UserCheck,
    private_session: Calendar,
    session_confirmed: CheckCircle,
    session_declined: XCircle,
    support_ticket: MessageSquare,
    campaign_sent: Mail,
    class_created: Calendar,
    schedule_created: Calendar,
    package_purchased: Package,
    studio_booking: Building2,
    event_booking: Ticket,
  };

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-6 md:mb-8 flex items-start justify-between">
        <div>
          <span className="font-cookie text-xl md:text-2xl text-empire-gold">Welcome back, {admin?.first_name || admin?.name || "Admin"}</span>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Dashboard</h1>
        </div>
        <button
          onClick={() => setShowRevenue(!showRevenue)}
          className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors mt-1 p-2 rounded-lg hover:bg-neutral-800"
        >
          {showRevenue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showRevenue ? "Hide" : "Show"} revenue
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const CardWrapper = stat.link ? Link : "div";
          const cardProps = stat.link ? { to: stat.link } : {};
          const isBlurred = stat.sensitive && !showRevenue;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <CardWrapper {...cardProps}>
                <Card className={`card-empire h-full ${stat.link ? "hover:border-empire-gold/40 transition-colors cursor-pointer" : ""}`}>
                  <CardContent className="p-3 md:p-4">
                    <div className="flex items-center justify-between mb-2 md:mb-3">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 md:w-5 md:h-5 ${stat.color}`} />
                      </div>
                    </div>
                    <h3 className={`text-lg md:text-2xl font-bold text-white truncate transition-all ${isBlurred ? "blur-md select-none" : ""}`}>
                      {stat.value}
                    </h3>
                    <p className="text-xs md:text-sm text-neutral-400 truncate">{stat.title}</p>
                    <p className="text-xs text-neutral-500 mt-1 hidden md:block">{stat.subtitle}</p>
                  </CardContent>
                </Card>
              </CardWrapper>
            </motion.div>
          );
        })}
      </div>

      {/* Reorderable sections */}
      <div className="flex flex-col gap-4 md:gap-6 mb-4 md:mb-6">
        {/* Quick Actions — first on mobile, second on desktop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="order-1 md:order-2"
        >
          <Card className="card-empire">
            <CardContent className="py-4 px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <span className="text-neutral-500 text-sm font-medium sm:mr-2 whitespace-nowrap">Quick Actions</span>
                <div className="flex flex-col sm:flex-row gap-3 flex-1">
                  <Link to="/admin/class-roster" className="flex-1">
                    <Button className="w-full justify-start gap-2 bg-empire-gold/10 hover:bg-empire-gold/20 text-empire-gold border border-empire-gold/30">
                      <ClipboardList className="w-4 h-4" />
                      Class Roster
                    </Button>
                  </Link>
                  <Link to="/admin/attendance" className="flex-1">
                    <Button variant="outline" className="w-full justify-start gap-2 border-neutral-700 hover:border-empire-gold/40">
                      <UserCheck className="w-4 h-4" />
                      Scan QR / Attendance
                    </Button>
                  </Link>
                  <Link to="/admin/campaigns" className="flex-1">
                    <Button variant="outline" className="w-full justify-start gap-2 border-neutral-700 hover:border-empire-gold/40">
                      <Mail className="w-4 h-4" />
                      Send Campaign
                    </Button>
                  </Link>
                  <Link to="/admin/schedules" className="flex-1">
                    <Button variant="outline" className="w-full justify-start gap-2 border-neutral-700 hover:border-empire-gold/40">
                      <Calendar className="w-4 h-4" />
                      Add Schedule
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Today's Attendance — second on mobile, first on desktop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="order-2 md:order-1"
        >
        <Card className="card-empire">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-white text-base md:text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-empire-gold" />
              Today's Attendance
            </CardTitle>
            <Link to="/admin/class-roster">
              <Button variant="ghost" size="sm" className="text-neutral-400 hover:text-empire-gold">
                View Roster <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {todayClasses.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No classes scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayClasses.slice(0, 5).map((schedule) => {
                  const totalBooked = schedule.bookings?.length || 0;
                  const checkedIn = schedule.checked_in_count || 0;
                  const attendanceRate = totalBooked > 0 ? Math.round((checkedIn / totalBooked) * 100) : 0;
                  
                  return (
                    <Link
                      key={schedule.id}
                      to={`/admin/class-roster?date=${format(new Date(), "yyyy-MM-dd")}&schedule=${schedule.id}`}
                      className="block"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 bg-neutral-900/50 rounded-xl border border-neutral-800 hover:border-empire-gold/30 transition-colors">
                        <div className="flex items-center gap-3 mb-2 sm:mb-0">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-empire-gold/20 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 md:w-6 md:h-6 text-empire-gold" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-white text-sm md:text-base truncate">
                              {schedule.dance_class?.name || "Class"}
                            </h4>
                            <p className="text-xs md:text-sm text-neutral-400">
                              {schedule.start_datetime && format(parseISO(schedule.start_datetime), "h:mm a")}
                              {schedule.instructor && ` • ${schedule.instructor.name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-4 pl-13 sm:pl-0">
                          <div className="text-center">
                            <p className="text-xs text-neutral-500">Booked</p>
                            <p className="font-bold text-white text-sm md:text-base">
                              {totalBooked}/{schedule.max_capacity || 20}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-neutral-500">Checked In</p>
                            <p className="font-bold text-green-400 text-sm md:text-base">
                              {checkedIn}
                            </p>
                          </div>
                          <div className="text-center hidden sm:block">
                            <p className="text-xs text-neutral-500">Rate</p>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                attendanceRate >= 80 
                                  ? "border-green-500/30 text-green-400" 
                                  : attendanceRate >= 50 
                                    ? "border-yellow-500/30 text-yellow-400"
                                    : "border-neutral-500/30 text-neutral-400"
                              }`}
                            >
                              {attendanceRate}%
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

        {/* Recent Activity — always last */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="order-3"
        >
          <Card className="card-empire">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-white text-base md:text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-empire-gold" />
                Recent Activity
              </CardTitle>
              <Link to="/admin/activities">
                <Button variant="ghost" size="sm" className="text-neutral-400 hover:text-empire-gold">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <div className="text-center py-6 text-neutral-500">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {activities.slice(0, 9).map((activity, index) => {
                    const Icon = activityIcons[activity.activity_type] || Activity;
                    return (
                      <motion.div
                        key={activity.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`flex items-start gap-3 p-3 bg-neutral-900/50 rounded-lg border border-neutral-800 ${
                          index >= 3 ? "hidden md:flex" : ""
                        }`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-neutral-800 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-empire-gold" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white font-medium truncate">{activity.title}</p>
                          <p className="text-xs text-neutral-500">
                            {activity.created_at && format(parseISO(activity.created_at), "MMM d, h:mm a")}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
